import type { QueryClient } from "@tanstack/react-query"

import {
  getTransportOperationsForCacheTags,
  type TransportOperationCacheTag,
  type TransportOperationName,
} from "@/types_api/backend/transport/operations"

import { invalidateTransportOperationQueryKeys } from "./transportQueryInvalidation"

interface TransportMutationInvalidation {
  cacheTags?: readonly TransportOperationCacheTag[]
  operations?: readonly TransportOperationName[]
  excludeOperations?: readonly TransportOperationName[]
  refetchType?: "active" | "none"
}

export interface TransportMutationCacheEffects {
  invalidations: readonly TransportMutationInvalidation[]
  invalidationsOnError?: readonly TransportMutationInvalidation[]
  waitForRefetch?: boolean
  invalidateOnError?: boolean
}

export interface TransportMutationCacheScope {
  queryClient: QueryClient
  organizationSlug: string | null
  workspaceId: string | null
}

const AGENT_READS: TransportMutationInvalidation = {
  cacheTags: ["agents"],
  // These readers have no generated cache tags.
  operations: ["getAgent", "getAgentNames"],
}
const TEST_SET_READS: TransportMutationInvalidation = {
  cacheTags: ["test-sets"],
  operations: [
    "listTestSets",
    "testSetMetadata",
    "testSetRecords",
    "getTestSetVersions",
    "listTestCases",
    "listTestSetTags",
    "testSetMetadataKeys",
    "getTestSetAgentAssociations",
  ],
}
const HOME_READS: TransportMutationInvalidation = {
  operations: ["homeActivation"],
}
const AGENT_EFFECTS: TransportMutationCacheEffects = {
  invalidations: [AGENT_READS],
}
const AGENT_CATALOG_EFFECTS: TransportMutationCacheEffects = {
  invalidations: [AGENT_READS, HOME_READS],
}
const AGENT_DELETE_EFFECTS: TransportMutationCacheEffects = {
  invalidationsOnError: [AGENT_READS, HOME_READS],
  invalidations: [
    {
      ...AGENT_READS,
      excludeOperations: [
        "listAgents",
        "listAllAgents",
        "getAgent",
        "getAgentVersions",
      ],
    },
    {
      operations: [
        "listAgents",
        "listAllAgents",
        "getAgent",
        "getAgentVersions",
      ],
      refetchType: "none",
    },
    HOME_READS,
  ],
}
const TEST_SET_EFFECTS: TransportMutationCacheEffects = {
  invalidations: [TEST_SET_READS, AGENT_READS],
}
const TEST_SET_CATALOG_EFFECTS: TransportMutationCacheEffects = {
  invalidations: [...TEST_SET_EFFECTS.invalidations, HOME_READS],
}
const AUTO_ADD_EFFECTS: TransportMutationCacheEffects = {
  invalidations: [
    {
      operations: [
        "getTestSetAutoAddRule",
        "listTestSetAutoAddPending",
        "getTestSetAutoAddSummary",
        "listTestSetAutoAddPendingCounts",
        "testSetRecords",
        "testSetMetadata",
        "listTestSets",
      ],
    },
  ],
}

const MUTATION_CACHE_EFFECTS: Partial<
  Record<TransportOperationName, TransportMutationCacheEffects>
> = {
  createAgent: AGENT_CATALOG_EFFECTS,
  duplicateAgent: AGENT_CATALOG_EFFECTS,
  updateAgent: AGENT_EFFECTS,
  deleteAgent: AGENT_DELETE_EFFECTS,
  deleteAgents: AGENT_DELETE_EFFECTS,
  restoreAgent: AGENT_CATALOG_EFFECTS,
  revertAgentVersion: AGENT_EFFECTS,
  addAgentMetrics: AGENT_EFFECTS,
  removeAgentMetric: AGENT_EFFECTS,
  setAgentMetrics: AGENT_EFFECTS,
  addAgentWorkflow: AGENT_EFFECTS,
  removeAgentWorkflow: AGENT_EFFECTS,
  createTestSet: TEST_SET_CATALOG_EFFECTS,
  duplicateTestSet: TEST_SET_CATALOG_EFFECTS,
  deleteTestSet: TEST_SET_CATALOG_EFFECTS,
  deleteTestSets: TEST_SET_CATALOG_EFFECTS,
  restoreTestSet: TEST_SET_CATALOG_EFFECTS,
  updateTestSet: TEST_SET_EFFECTS,
  updateTestCaseRow: TEST_SET_EFFECTS,
  addTestSetRows: TEST_SET_EFFECTS,
  deleteTestSetRow: TEST_SET_EFFECTS,
  saveTestSetEditorState: TEST_SET_EFFECTS,
  addTestSetAgentAssociations: TEST_SET_EFFECTS,
  removeTestSetAgentAssociation: TEST_SET_EFFECTS,
  revertTestSetVersion: TEST_SET_EFFECTS,
  saveTestSetAutoAddRule: { ...AUTO_ADD_EFFECTS, waitForRefetch: true },
  deleteTestSetAutoAddRule: { ...AUTO_ADD_EFFECTS, waitForRefetch: true },
  acceptTestSetAutoAddSuggestion: {
    ...AUTO_ADD_EFFECTS,
    invalidateOnError: true,
  },
  rejectTestSetAutoAddSuggestion: {
    ...AUTO_ADD_EFFECTS,
    invalidateOnError: true,
  },
}

export function getTransportMutationCacheEffects(
  operation: TransportOperationName,
): TransportMutationCacheEffects | undefined {
  return MUTATION_CACHE_EFFECTS[operation]
}

export function applyTransportMutationCacheEffects(
  scope: TransportMutationCacheScope,
  effects: TransportMutationCacheEffects,
  outcome: "success" | "error" = "success",
): Promise<void> {
  const invalidations =
    outcome === "error"
      ? (effects.invalidationsOnError ?? effects.invalidations)
      : effects.invalidations
  return Promise.all(
    invalidations.map((invalidation) => {
      const excluded = new Set(invalidation.excludeOperations)
      const operations = [
        ...new Set([
          ...getTransportOperationsForCacheTags(invalidation.cacheTags ?? []),
          ...(invalidation.operations ?? []),
        ]),
      ].filter((operation) => !excluded.has(operation))

      return invalidateTransportOperationQueryKeys(
        scope.queryClient,
        scope.organizationSlug,
        operations,
        scope.workspaceId,
        { refetchType: invalidation.refetchType },
      )
    }),
  ).then(() => undefined)
}
